import type { NodeV2 } from '@vedalib/editor'
import { isNodeV2, isShellNode } from '@vedalib/editor'
import { gqlClient } from 'gql'
import lodash from 'lodash'
import * as R from 'ramda'
import type React from 'react'
import { useCallback, useMemo } from 'react'

import { NO_PANEL_ELEMENTS } from 'components/Editor/EditorElements/elements.config'
import { onDrag } from 'components/Editor/NodeV2/commands/onDrag'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { EMPTY_ARRAY } from 'constants/commonConstans'
import { useBlocksCopy } from 'gql/blocks/apollo'
import { blocksGetById } from 'gql/blocks/gql/queries'
import { ScrollContainerEnum } from 'services/Scroll/enums'
import {
  setProjectNavigation,
  setNavbar,
  updateBlock,
  setToolbar,
  updateNodeState,
} from 'services/Store/Project/actions'
import { AppMode, NavigationEnum } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getBlock,
  getNavBar,
  getNodeId,
  getSection,
  getSectionId,
  getSelectedBlocksIds,
  getSelectedNodeIds,
} from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import store from 'services/Store/store'
import { t } from 'services/Translation'

import { isRoot } from '../NodeV2/guards'
import type { ICopyData } from './EditorHotkeys'
import { pasteNode } from './mutators/block'
import { getDeepChildrenIds, getElementByNode, getNodeSafe, getParentNode } from './selectors/block'

const getTargets = (
  block?: Block | null,
  propNodes?: NodeV2[] | null,
  selectedNodes: string[] = EMPTY_ARRAY,
) => {
  let nodes = propNodes
  let nodeIds = nodes?.map(({ id }) => id) || EMPTY_ARRAY

  const selectedChildren = selectedNodes.flatMap((id) =>
    block ? R.uniq([id, ...getDeepChildrenIds(block, id)]) : [],
  )
  const intersection = R.intersection(selectedChildren, nodeIds)
  if (
    intersection.length &&
    selectedNodes.every((id) => !isRoot(block!, block!.schema.nodes[id] as NodeV2))
  ) {
    nodes = selectedNodes.map((id) => block?.schema.nodes[id]).filter(isNodeV2)
    nodeIds = nodes.map(({ id }) => id)
  }
  const source = getParentNode(nodes?.[0]?.id, block)

  return { targets: nodes || EMPTY_ARRAY, source }
}

interface IArgs {
  mode: AppMode | null
  blockId: string | null
  nodeId: string | null
  setCustomMode?: React.Dispatch<React.SetStateAction<AppMode | null>>
}

export const useEditorCanvasMouse = ({ mode, blockId, nodeId, setCustomMode }: IArgs) => {
  const dispatch = useProjectDispatch()

  const onMouseDown = useCallback(
    (event: React.MouseEvent) => {
      const state = store.getState().project
      const navbar = getNavBar(state)
      const block = getBlock(state, { id: blockId })
      const selectedNodes = getSelectedNodeIds(state)
      const { targets, source } = getTargets(
        block,
        [block?.schema.nodes[nodeId || '']].filter(isNodeV2),
        selectedNodes,
      )

      onDrag(event, { mode, blockId, nodeId, targets, source })

      if (navbar.tab === NavigationEnum.project) {
        dispatch(setNavbar({ tab: null }))
      }
    },
    [dispatch, mode, blockId, nodeId],
  )

  const onDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()

      const state = store.getState().project
      const block = getBlock(state, { id: blockId })
      const node = getNodeSafe(block, nodeId)
      const element = getElementByNode(block, node)

      const isNoPanelElement = element?.type && NO_PANEL_ELEMENTS.includes(element?.type)

      if (mode === AppMode.pro && isShellNode(node)) {
        dispatch(updateNodeState({ id: node.id, state: { fill: true } }))
        setCustomMode?.(AppMode.fill)
      }

      const isPanel = element?.type && !NO_PANEL_ELEMENTS.includes(element?.type)
      if (isPanel && mode === AppMode.fill) {
        dispatch(
          setToolbar({ commentTab: null, taskTab: null, blockSettings: null, hidden: false }),
        )
      }

      if (!isNoPanelElement && mode === AppMode.fill) {
        dispatch(
          setToolbar({
            commentTab: null,
            taskTab: null,
            blockSettings: null,
            sectionSettings: null,
            hidden: false,
          }),
        )
      } else {
        dispatch(setToolbar({ commentTab: null, taskTab: null, blockSettings: null, hidden: true }))
      }
    },
    [dispatch, setCustomMode, mode, blockId, nodeId],
  )

  return useMemo(() => ({ onMouseDown, onDoubleClick }), [onMouseDown, onDoubleClick])
}

export const useBlocksDuplicate = (paramsSectionId?: string) => {
  const dispatch = useProjectDispatch()
  const secId = useProjectContext(getSectionId)
  const sectionId = paramsSectionId || secId
  const [copyBlocks] = useBlocksCopy(sectionId)

  return useCallback(
    async (copedBlockIds?: string[]) => {
      const projectState = store.getState().project
      const selectedBlockIds = getSelectedBlocksIds(projectState)
      const { blocksOrder } = getSection(projectState) || { blocksOrder: [] as string[] }
      const orderedSelectedBlocks = blocksOrder.filter((id) => selectedBlockIds.includes(id))
      const lastSelected = orderedSelectedBlocks[orderedSelectedBlocks.length - 1]
      const lastSelectedIndex = blocksOrder.findIndex((id) => id === lastSelected)
      const uuids = lodash.sortBy(copedBlockIds || selectedBlockIds, (id) =>
        blocksOrder.indexOf(id),
      )
      const res = await copyBlocks({
        variables: {
          payload: {
            uuids,
            activeBlockUUID: lastSelected || null,
            sectionId: sectionId || '',
          },
        },
      })
      const newBlocksOrder = res.data?.data.blocksOrder
      if (newBlocksOrder) {
        notify({
          message: t('notify.pasteBlockToAnotherSection.success', {
            count: selectedBlockIds.length,
          }),
          type: NotificationType.success,
        })
        const newSelectedBlocks = newBlocksOrder.slice(
          lastSelectedIndex + 1,
          lastSelectedIndex + selectedBlockIds.length + 1,
        )
        if (res.data?.data.blocks.length) {
          const blocksIds = [...(res?.data?.data?.blocks || [])].map((b) => b.uuid)
          dispatch(
            setProjectNavigation({
              blockId: blocksIds[0],
              selectedBlocks: blocksIds,
              nodeIds: null,
              sectionId,
              scroll: [
                {
                  container: ScrollContainerEnum.canvas,
                  id: newSelectedBlocks[0],
                  block: 'begin',
                  scroll: ['never', 'always'],
                },
                {
                  container: ScrollContainerEnum.blocksNav,
                  id: newSelectedBlocks[0],
                  block: 'begin',
                  scroll: 'always',
                },
              ],
            }),
          )
          dispatch(setNavbar({ tab: NavigationEnum.section }))
        }
      }
    },
    [copyBlocks, dispatch, sectionId],
  )
}

export const useNodeDuplicate = () => {
  const dispatch = useProjectDispatch()
  const block = useProjectContext(getBlock)
  const nodeId = useProjectContext(getNodeId)

  const onChange = useCallback(
    async (value: unknown) => block?.uuid && dispatch(updateBlock({ id: block.uuid, value })),
    [dispatch, block],
  )

  return useCallback(
    async (data: ICopyData) => {
      const blockData = await gqlClient.core.query<{ data: Block }>({
        query: blocksGetById,
        variables: {
          uuid: data.blockId || '',
          sectionId: data.sectionId || '',
        },
      })

      const fromBlock = blockData.data.data
      const fromNodeId = data.nodeIds?.[0]

      if (fromNodeId && fromBlock && nodeId && block) {
        const update = pasteNode(fromBlock, block, fromNodeId, nodeId, false)
        if (update) {
          onChange(update.block)
        } else {
          notify({
            message: 'Paste failed',
            type: NotificationType.info,
          })
        }
      }
    },
    [block, nodeId, onChange],
  )
}
